import React, { useEffect, useState } from 'react';
import {
  Modal,
  Header,
  Rating,
  Form,
  Button,
  TextArea,
  Divider,
  Icon,
  Loader,
} from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { withTranslation } from 'react-i18next';
import { authUtil, uxAnalyticsUtil } from '../../utils';
import Avatar from '../../components/avatar/avatar';

interface Props {
  open: boolean;
  onClose: () => void;
  reader: any;
  onRatingUpdated?: () => void;
  t: (key: string, options?: any) => string;
}

const BookSpecificRating: React.FC<Props> = ({
  open,
  onClose,
  reader,
  onRatingUpdated,
  t,
}) => {
  const currentBook = useSelector(state => state.book.currentBook);

  const [isLoading, setIsLoading] = useState(false);
  const [rating, setRating] = useState<number>(
    reader?.authorRating?.rating || 0
  );
  const [text, setText] = useState<string>(reader?.authorRating?.text || '');
  const [existingRating, setExistingRating] = useState<any>(
    reader?.authorRating || null
  );

  useEffect(() => {
    if (open && reader) {
      setExistingRating(reader.authorRating || null);
      setRating(reader.authorRating?.rating || 0);
      setText(reader.authorRating?.text || '');
    }
  }, [open, reader]);

  const handleSubmit = async () => {
    if (!reader?.user || !currentBook || rating === 0) {
      toast.error(t('PleaseSelectRating'));
      return;
    }

    setIsLoading(true);
    try {
      const idToken = await authUtil.getFreshIdToken();

      uxAnalyticsUtil.trackEvent({
        category: 'UserReviews',
        action: 'Submitted book-specific review',
        value: rating,
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/users/${reader.user._id}/reviews`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': idToken,
          },
          body: JSON.stringify({
            rating,
            text,
            bookId: currentBook._id,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        toast.success(t('RatingSubmittedSuccessfully'));
        if (onRatingUpdated) {
          onRatingUpdated();
        }
        onClose();
      } else {
        throw new Error(result.msg || t('FailedToSubmitRating'));
      }
    } catch (error) {
      console.error('Failed to submit rating:', error);
      toast.error(error instanceof Error ? error.message : t('FailedToSubmitRating'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setText('');
    setExistingRating(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="small" closeIcon>
      <Modal.Header>
        <Icon name="star" color="yellow" />
        {t('RateReaderForBook', { title: currentBook?.title })}
      </Modal.Header>

      <Modal.Content>
        {isLoading ? (
          <Loader active inline="centered" />
        ) : (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <Avatar
                name={reader?.user?.displayName}
                src={reader?.user?.photoUrl}
                diameter={40}
              />
              <div style={{ marginLeft: '15px' }}>
                <Header as="h4" style={{ margin: 0 }}>
                  {reader?.user?.displayName}
                </Header>
                <p
                  style={{
                    margin: '5px 0 0 0',
                    color: '#666',
                    fontSize: '14px',
                  }}
                >
                  {reader?.completedBookAt
                    ? t('CompletedOnDate', {
                        date: new Date(
                          reader.completedBookAt
                        ).toLocaleDateString(),
                      })
                    : t('ReaderForThisBook')}
                </p>
              </div>
            </div>

            <Divider />

            <Form>
              <Form.Field>
                <label>{t('YourRating')}</label>
                <Rating
                  icon="star"
                  rating={rating}
                  maxRating={5}
                  size="huge"
                  onRate={(e, { rating }) => setRating(rating as number)}
                />
                <p
                  style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}
                >
                  {rating === 0 && t('ClickToRateReader')}
                  {rating === 1 && t('RatingPoor')}
                  {rating === 2 && t('RatingFair')}
                  {rating === 3 && t('RatingGood')}
                  {rating === 4 && t('RatingVeryGood')}
                  {rating === 5 && t('RatingExcellent')}
                </p>
              </Form.Field>

              <Form.Field>
                <label>{t('CommentsOptional')}</label>
                <TextArea
                  placeholder={t('ShareExperiencePlaceholder')}
                  value={text}
                  onChange={(e, { value }) => setText(value as string)}
                  rows={3}
                />
              </Form.Field>
            </Form>
          </div>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={handleClose} disabled={isLoading}>
          {t('Cancel')}
        </Button>
        <Button
          primary
          onClick={handleSubmit}
          disabled={isLoading || rating === 0}
          loading={isLoading}
        >
          {existingRating ? t('UpdateRating') : t('SubmitRating')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default withTranslation()(BookSpecificRating);
